<template>
  <div class="container">
    <form
      v-if="!success"
      @submit.prevent="isUpdate ? updateLineId() : createLineId()"
    >
      <Toolbar class="mt-2 mb-2">
        <template #start>
          <h2 v-if="!lineIdProp">
            <span v-if="isUpdate">
              {{ $t('lineidpage.update') }}
            </span>
            <span v-else>{{ $t('lineidpage.create') }}</span>
          </h2>
        </template>
      </Toolbar>

      <div class="mt-2 grid">
        <h2 class="col-12">
          <strong>{{ $t('lineidpage.fieldset.information') }}</strong>
        </h2>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="requestId"
            class="required-input"
          >{{ $t('entity.lineId.requestId') }}</label>
          <InputNumber
            id="requestId"
            v-model="v$.lineId.requestId.$model"
            class="col-12 mt-2 p-0"
            :class="{'invalid-input': v$.lineId.requestId.$error }"
            :min="1"
            autocomplete="on"
            required
          />
          <div
            v-for="error of v$.lineId.requestId.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="requestDate"
            class="required-input"
          >{{ $t('entity.lineId.requestDate') }}</label>
          <InputText
            id="requestDate"
            v-model.trim="v$.lineId.requestDate.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.requestDate.$error }"
            type="date"
            autocomplete="on"
            required
          />
          <div
            v-for="error of v$.lineId.requestDate.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="mainVenue"
            class="required-input"
          >{{ $t('entity.lineId.mainVenue') }}</label>
          <Dropdown
            id="mainVenue"
            v-model="v$.lineId.mainVenue.$model"
            :options="venues"
            :option-label="(value) => $i18n.locale === 'fr' ? value.frenchWording : value.englishWording"
            :placeholder="$t('lineidpage.dropdown.location')"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.mainVenue.$error }"
            :disabled="lineIdProp !== null"
            required
          />
          <div
            v-for="error of v$.lineId.mainVenue.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="otherVenues">{{ $t('entity.lineId.otherVenues') }}</label>
          <InputText
            id="otherVenues"
            v-model.trim="v$.lineId.otherVenues.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.otherVenues.$error }"
            type="text"
            autocomplete="on"
            :disabled="lineIdProp"
          />
          <div
            v-for="error of v$.lineId.otherVenues.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12">
          <label for="specificLocation">{{ $t('entity.lineId.specificLocation') }}</label>
          <InputText
            id="specificLocation"
            v-model.trim="v$.lineId.specificLocation.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.specificLocation.$error }"
            type="text"
            autocomplete="on"
            :disabled="lineIdProp"
          />
          <div
            v-for="error of v$.lineId.specificLocation.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="stakeholderId"
            class="required-input"
          >{{ $t('entity.lineId.stakeholderId') }}</label>
          <InputText
            id="stakeholderId"
            v-model.trim="v$.lineId.stakeholderId.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.stakeholderId.$error }"
            type="text"
            autocomplete="on"
            required
            :disabled="lineIdProp"
          />
          <div
            v-for="error of v$.lineId.stakeholderId.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="licencePeriod"
            class="required-input"
          >{{ $t('entity.lineId.licencePeriod') }}</label>
          <Dropdown
            id="licencePeriod"
            v-model="v$.lineId.licencePeriod.$model"
            :options="licencePeriods"
            :option-label="(value) => $t(`entity.lineId.licencePeriods.${value}`)"
            :placeholder="$t('lineidpage.dropdown.licencePeriod')"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.licencePeriod.$error }"
            required
          />
          <div
            v-for="error of v$.lineId.licencePeriod.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="startDate"
            class="required-input"
          >{{ $t('entity.lineId.startDate') }}</label>
          <InputText
            id="startDate"
            v-model.trim="v$.lineId.startDate.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.startDate.$error }"
            type="date"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.startDate.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="endDate"
            class="required-input"
          >{{ $t('entity.lineId.endDate') }}</label>
          <InputText
            id="endDate"
            v-model.trim="v$.lineId.endDate.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.endDate.$error }"
            type="date"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.endDate.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12">
          <label for="remarks">{{ $t('entity.lineId.remarks') }}</label>
          <InputText
            id="remarks"
            v-model.trim="v$.lineId.remarks.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.remarks.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.remarks.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2 grid">
        <h2 class="col-12">
          <strong>{{ $t('lineidpage.fieldset.frequency') }}</strong>
        </h2>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="usageType"
            class="required-input"
          >{{ $t('entity.lineId.usageType') }}</label>
          <Dropdown
            id="usageType"
            v-model="v$.lineId.usageType.$model"
            :options="usageTypes"
            :option-label="(value) => $t(`entity.lineId.usageTypes.${value}`)"
            :placeholder="$t('lineidpage.dropdown.usageTypes')"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.usageType.$error }"
            required
          />
          <div
            v-for="error of v$.lineId.usageType.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12">
          <label
            for="duplex"
            class="required-input"
          >{{ $t('entity.lineId.duplex') }}</label>
          <Checkbox
            id="duplex"
            v-model="v$.lineId.duplex.$model"
            class="p-0 mt-2 ml-2"
            :class="{'invalid-input': v$.lineId.duplex.$error }"
            :disabled="lineIdProp !== null"
            :binary="true"
          />
          <div
            v-for="error of v$.lineId.duplex.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="tuningRangeFrom"
            class="required-input"
          >{{ $t('entity.lineId.tuningRangeFrom') }}</label>
          <InputNumber
            id="tuningRangeFrom"
            v-model="v$.lineId.tuningRangeFrom.$model"
            mode="decimal"
            :min-fraction-digits="1"
            :max-fraction-digits="5"
            :min="1"
            :max="999999"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.tuningRangeFrom.$error }"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.tuningRangeFrom.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="tuningRangeTo"
            class="required-input"
          >{{ $t('entity.lineId.tuningRangeTo') }}</label>
          <InputNumber
            id="tuningRangeTo"
            v-model="v$.lineId.tuningRangeTo.$model"
            mode="decimal"
            :min-fraction-digits="1"
            :max-fraction-digits="5"
            :min="1"
            :max="999999"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.tuningRangeTo.$error }"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.tuningRangeTo.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="channelBandwidth"
            class="required-input"
          >{{ $t('entity.lineId.channelBandwidth') }}</label>
          <InputNumber
            id="channelBandwidth"
            v-model="v$.lineId.channelBandwidth.$model"
            mode="decimal"
            :min-fraction-digits="1"
            :max-fraction-digits="5"
            :min="0"
            :max="999999"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.channelBandwidth.$error }"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.channelBandwidth.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="tuningStep"
            class="required-input"
          >{{ $t('entity.lineId.tuningStep') }}</label>
          <InputNumber
            id="tuningStep"
            v-model="v$.lineId.tuningStep.$model"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.tuningStep.$error }"
            autocomplete="on"
            :disabled="lineIdProp"
            :min="1"
            :max="999999"
            required
          />
          <div
            v-for="error of v$.lineId.tuningStep.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="duplexHigh"
            class="required-input"
          >{{ $t('entity.lineId.duplexHigh') }}</label>
          <InputText
            id="duplexHigh"
            v-model.trim="v$.lineId.duplexHigh.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.duplexHigh.$error }"
            type="text"
            autocomplete="on"
            required
          />
          <div
            v-for="error of v$.lineId.duplexHigh.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="duplexLow"
            class="required-input"
          >{{ $t('entity.lineId.duplexLow') }}</label>
          <InputText
            id="duplexLow"
            v-model.trim="v$.lineId.duplexLow.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.duplexLow.$error }"
            type="text"
            autocomplete="on"
            required
          />
          <div
            v-for="error of v$.lineId.duplexLow.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="transmissionPower"
            class="required-input"
          >{{ $t('entity.lineId.transmissionPower') }}</label>
          <InputNumber
            id="transmissionPower"
            v-model="v$.lineId.transmissionPower.$model"
            mode="decimal"
            :min-fraction-digits="1"
            :max-fraction-digits="5"
            :min="0"
            :max="99999"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.transmissionPower.$error }"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.transmissionPower.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="status"
            class="required-input"
          >{{ $t('entity.lineId.status') }}</label>
          <Dropdown
            id="status"
            v-model="v$.lineId.status.$model"
            :options="status"
            :option-label="(value) => $t(`entity.lineId.statusOptions.${value.toLowerCase()}`)"
            :placeholder="$t('lineidpage.dropdown.status')"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.status.$error }"
            :disabled="lineIdProp !== null"
            required
          />
          <div
            v-for="error of v$.lineId.status.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2 grid">
        <h2 class="col-12">
          <strong>{{ $t('lineidpage.fieldset.equipment') }}</strong>
        </h2>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="spectrumService"
            class="required-input"
          >{{ $t('entity.lineId.spectrumService') }}</label>
          <InputText
            id="spectrumService"
            v-model.trim="v$.lineId.spectrumService.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.spectrumService.$error }"
            type="text"
            autocomplete="on"
            :disabled="lineIdProp"
            required
          />
          <div
            v-for="error of v$.lineId.spectrumService.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="spectrumServiceOther">{{ $t('entity.lineId.spectrumServiceOther') }}</label>
          <InputText
            id="spectrumServiceOther"
            v-model.trim="v$.lineId.spectrumServiceOther.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.spectrumServiceOther.$error }"
            type="text"
            autocomplete="on"
            :disabled="lineIdProp"
          />
          <div
            v-for="error of v$.lineId.spectrumServiceOther.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="manufacturer">{{ $t('entity.lineId.manufacturer') }}</label>
          <InputText
            id="manufacturer"
            v-model.trim="v$.lineId.manufacturer.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.manufacturer.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.manufacturer.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="equipmentReference">{{ $t('entity.lineId.equipmentReference') }}</label>
          <InputText
            id="equipmentReference"
            v-model.trim="v$.lineId.equipmentReference.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.equipmentReference.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.equipmentReference.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="numberOfEquipment"
            class="required-input"
          >{{ $t('entity.lineId.numberOfEquipment') }}</label>
          <InputNumber
            id="numberOfEquipment"
            v-model="v$.lineId.numberOfEquipment.$model"
            class="col-12 p-0 mt-2"
            :min="1"
            :max="9999"
            :class="{'invalid-input': v$.lineId.numberOfEquipment.$error }"
            required
          />
          <div
            v-for="error of v$.lineId.numberOfEquipment.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2 grid">
        <h2 class="col-12">
          <strong>{{ $t('lineidpage.fieldset.antenna') }}</strong>
        </h2>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="antennaReference">{{ $t('entity.lineId.antennaReference') }}</label>
          <InputText
            id="antennaReference"
            v-model.trim="v$.lineId.antennaReference.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.antennaReference.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.antennaReference.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="satelliteName">{{ $t('entity.lineId.satelliteName') }}</label>
          <InputText
            id="satelliteName"
            v-model.trim="v$.lineId.satelliteName.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.satelliteName.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.satelliteName.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb- col-12 md:col-6">
          <label for="antennaHalfPowerAngle">{{ $t('entity.lineId.antennaHalfPowerAngle') }}</label>
          <InputText
            id="antennaHalfPowerAngle"
            v-model.trim="v$.lineId.antennaHalfPowerAngle.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.antennaHalfPowerAngle.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.antennaHalfPowerAngle.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="antennaDiameter">{{ $t('entity.lineId.antennaDiameter') }}</label>
          <InputNumber
            id="antennaDiameter"
            v-model="v$.lineId.antennaDiameter.$model"
            mode="decimal"
            :min-fraction-digits="0"
            :max-fraction-digits="2"
            :min="0"
            :max="999"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.antennaDiameter.$error }"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.antennaDiameter.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
        <div class="p-input mb-3 col-12 md:col-6">
          <label
            for="antennaHeightAboveGround"
            class="required-input"
          >{{ $t('entity.lineId.antennaHeightAboveGround') }}</label>
          <InputNumber
            id="antennaHeightAboveGround"
            v-model="v$.lineId.antennaHeightAboveGround.$model"
            class="col-12 p-0 mt-2"
            :class="{'invalid-input': v$.lineId.antennaHeightAboveGround.$error }"
            :min="0"
            :max="999999"
            autocomplete="on"
            required
          />
          <div
            v-for="error of v$.lineId.antennaHeightAboveGround.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2 grid">
        <h2 class="col-12">
          <strong>{{ $t('lineidpage.fieldset.wifi') }}</strong>
        </h2>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="standardTechnology">{{ $t('entity.lineId.standardTechnology') }}</label>
          <InputText
            id="standardTechnology"
            v-model.trim="v$.lineId.standardTechnology.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.standardTechnology.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.standardTechnology.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="p-input mb-3 col-12 md:col-6">
          <label for="wirelessLanSsid">{{ $t('entity.lineId.wirelessLanSsid') }}</label>
          <InputText
            id="wirelessLanSsid"
            v-model.trim="v$.lineId.wirelessLanSsid.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.wirelessLanSsid.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.wirelessLanSsid.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
        <div class="p-input mb-3 col-12 md:col-6">
          <label for="wirelessLanMacAddress">{{ $t('entity.lineId.wirelessLanMacAddress') }}</label>
          <InputText
            id="wirelessLanMacAddress"
            v-model.trim="v$.lineId.wirelessLanMacAddress.$model"
            class="col-12 mt-2"
            :class="{'invalid-input': v$.lineId.wirelessLanMacAddress.$error }"
            type="text"
            autocomplete="on"
          />
          <div
            v-for="error of v$.lineId.wirelessLanMacAddress.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <Message
        v-if="errors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error.content"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toLinesTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          :disabled="createButtonDisabled"
          :loading="isSaving"
          class="p-button-rounded p-button-outlined"
        />
      </div>
    </form>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Dropdown from 'primevue/dropdown';
import {useVuelidate} from '@vuelidate/core';
import {required} from '@/i18n/i18nValidators';
import {LINEID_STATUS} from "@/shared/constants";
import Toolbar from "primevue/toolbar";
import Checkbox from "primevue/checkbox";
import {reactive} from "vue";
import useVenueFilter from "@/composables/filters/useVenueFilter";

export default {
  name: 'LineIdForm',
  components: {Toolbar, InputText, InputNumber, Button, Message, Dropdown, Checkbox},
  props: {
    lineIdProp: {
      type: Object,
      default: null
    }
  },
  emits: ['refresh'],
  setup() {
    const venueFilter = reactive(useVenueFilter());
    return {
      v$: useVuelidate(),
      venueFilter: venueFilter,
    }
  },
  data() {
    return {
      lineId: {
        stakeholderId: null,
        requestId: null,
        requestDate: null,
        licencePeriod: null,
        startDate: null,
        endDate: null,
        spectrumService: null,
        spectrumServiceOther: null,
        mainVenue: null,
        otherVenues: null,
        specificLocation: null,
        usageType: null,
        duplex: false,
        tuningRangeTo: null,
        tuningRangeFrom: null,
        channelBandwidth: null,
        tuningStep: null,
        transmissionPower: null,
        manufacturer: null,
        equipmentReference: null,
        numberOfEquipment: null,
        antennaReference: null,
        satelliteName: null,
        antennaHalfPowerAngle: null,
        antennaDiameter: null,
        antennaHeightAboveGround: null,
        standardTechnology: null,
        wirelessLanSsid: null,
        wirelessLanMacAddress: null,
        duplexHigh: null,
        duplexLow: null,
        status: null,
        remarks: null,
      },
      errors: [],
      success: false,
      isSaving: false,
      isUpdate: false,
      status: LINEID_STATUS,
      venues: [],
      usageTypes: [],
      licencePeriods: [],
    }
  },
  computed: {
    createButtonDisabled() {
      return this.v$.$invalid;
    }
  },
  validations() {
    return {
      lineId: {
        requestId: {required},
        stakeholderId: {required},
        requestDate: {required},
        licencePeriod: {required},
        startDate: {required},
        endDate: {required},
        spectrumService: {required},
        spectrumServiceOther: {},
        mainVenue: {required},
        otherVenues: {},
        specificLocation: {},
        usageType: {required},
        duplex: {required},
        tuningRangeTo: {required},
        tuningRangeFrom: {required},
        channelBandwidth: {required},
        tuningStep: {required},
        transmissionPower: {required},
        manufacturer: {},
        equipmentReference: {},
        numberOfEquipment: {required},
        antennaReference: {},
        satelliteName: {},
        antennaHalfPowerAngle: {},
        antennaDiameter: {},
        antennaHeightAboveGround: {required},
        standardTechnology: {},
        wirelessLanSsid: {},
        wirelessLanMacAddress: {},
        duplexHigh: {required},
        duplexLow: {required},
        status: {required},
        remarks: {},
      },
    }
  },
  async mounted() {
    if (this.$route.params.id) {
      this.fetchLineId(this.$route.params.id)
      this.isUpdate = true;
    }

    if (this.lineIdProp) {
      Object.assign(this.lineId, this.lineIdProp)
      this.isUpdate = true;
    }
    this.venues = (await this.$venueService.fetchVenuesPagingAndSorting(this.venueFilter.getQueryParams())).content;
    this.usageTypes = this.$store.getters['setting/getLineIdUsageTypes'];
    this.licencePeriods = this.$store.getters['setting/getLineIdLicencePeriods'].slice().sort((a,b) => {
      const nameA = this.$t(`entity.lineId.licencePeriods.${a}`).toLowerCase();
      const nameB = this.$t(`entity.lineId.licencePeriods.${b}`).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  },
  methods: {
    async fetchLineId(id) {
      this.lineId = await this.$lineIdService.fetchLineId(id)
    },
    async createLineId() {
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      try {
        const created = await this.$lineIdService.createLineId(this.lineId)
        if (created) {
          this.success = true;
          this.$emitter.emit('toast', {severity: 'success', message: 'lineidpage.createSuccess'})
          this.isSaving = false;
          this.toLinesTable()
        }
      } catch (err) {
        this.success = false;
        this.errors = this.$t(err.response.data);
        this.isSaving = false;
      }
    },
    async updateLineId() {
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      try {
        const updated = await this.$lineIdService.updateLineId(this.lineId)
        if (updated) {
          this.success = true;
          this.$emitter.emit('toast', {severity: 'success', message: 'lineidpage.updateSuccess'})
          this.isSaving = false;
          this.toLinesTable()
        }
      } catch (err) {
        this.success = false;
        this.errors = this.$t(err.response.data);
        this.isSaving = false;
      }
    },
    toLinesTable() {
      if (!this.lineIdProp) {
        this.$router.push({name: 'lineId'})
      } else {
        this.$emit("refresh")
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep(.p-chips .p-chips-multiple-container) {
  width: 100%;
}
</style>
